import type { CapabilityCategoryDef } from '@tandemone/types'
import { ChevronRight } from 'lucide-react'

import { labelMatches } from './capabilities-derive'
import { CapabilityGroupSection } from './capability-group-section'
import { useCapabilitiesForm } from './use-capabilities-form'

/** a top-level category: a collapsible header with its selected-count and the
 *  group sections. hidden entirely when no descendant leaf matches the query. */
export function CapabilityCategorySection({
  category
}: {
  category: CapabilityCategoryDef
}) {
  const { state, derived, dispatch } = useCapabilitiesForm()

  const anyVisible = category.groups.some(group =>
    group.items.some(leaf =>
      labelMatches(state.query, leaf.label, group.label, category.label)
    )
  )
  if (!anyVisible) return null

  const count = derived.countsByCategory[category.key] ?? 0
  const open =
    state.query !== '' || (state.expandedCategories[category.key] ?? false)

  return (
    <div className='rounded-xl border border-border bg-card'>
      <button
        type='button'
        onClick={() =>
          dispatch({ type: 'toggleCategory', categoryKey: category.key })
        }
        className='flex w-full cursor-pointer items-center justify-between gap-2 px-2 py-3 text-left sm:px-4'
      >
        <span className='flex items-center gap-2 text-[13px] font-semibold tracking-wide text-ink normal-case sm:uppercase'>
          <ChevronRight
            className={`size-4 shrink-0 text-ink-muted transition-transform ${
              open ? 'rotate-90' : ''
            }`}
          />
          {category.label}
        </span>
        {count > 0 && (
          <span className='shrink-0 text-[12px] font-medium text-accent'>
            {count} selected
          </span>
        )}
      </button>
      {open && (
        <div className='flex flex-col gap-2 px-2 pb-4 sm:px-4'>
          {category.groups.map(group => (
            <CapabilityGroupSection
              key={group.key}
              group={group}
              categoryLabel={category.label}
            />
          ))}
        </div>
      )}
    </div>
  )
}
